/**
 * Client-side wishlist.
 *
 * The list lives in localStorage. Heart clicks never hit the server; PHP is only called to sync a logged-in
 * customer's account (debounced add/remove ops), to share a list, and to render the wishlist page tiles.
 *
 * Pages carry no per-user data (so they can be publicly cached): login state comes from the `swan_uid` cookie,
 * and the sync nonce from the uncached `swan_wishlist_state` endpoint.
 *
 * One delegated click listener handles every button, so tiles added later (SpeedSearch pagination, infinite
 * scroll, quick view) need no binding; paint() only sets classes and binds nothing, so it is safe to run repeatedly.
 *
 * @see includes/class-wishlist.php
 */
( function() {
	'use strict';

	const STORAGE_KEY = 'swan-wishlist';
	const SHARE_KEY   = 'swan-wishlist-share';
	const NONCE_KEY   = 'swan-wishlist-nonce';
	const SYNC_DELAY  = 2000;

	const config = window.swanWishlistConfig || {};
	const i18n   = config.i18n || {};

	let hadStoredState = false;
	let state          = loadState();
	let inFlight       = null;
	let syncTimer      = null;
	let addedThisPage  = [];

	/*
	 * Storage.
	 */

	function normalizeIds( ids ) {
		const result = [];

		( Array.isArray( ids ) ? ids : [] ).forEach( function( id ) {
			id = parseInt( id, 10 );
			if ( id > 0 && result.indexOf( id ) === -1 ) {
				result.push( id );
			}
		} );

		return result;
	}

	function emptyPending() {
		return { add: [], remove: [] };
	}

	function emptyState( owner ) {
		return { owner: owner, items: [], pending: emptyPending() };
	}

	function loadState() {
		try {
			const parsed = JSON.parse( localStorage.getItem( STORAGE_KEY ) );

			if ( parsed && Array.isArray( parsed.items ) ) {
				hadStoredState = true;

				const pending = parsed.pending || {};

				return {
					owner:   'string' === typeof parsed.owner ? parsed.owner : 'guest',
					items:   normalizeIds( parsed.items ),
					pending: { add: normalizeIds( pending.add ), remove: normalizeIds( pending.remove ) },
				};
			}
		} catch ( e ) {}

		return emptyState( 'guest' );
	}

	function saveState() {
		try {
			localStorage.setItem( STORAGE_KEY, JSON.stringify( state ) );
			hadStoredState = true;
		} catch ( e ) {}
	}

	function readSession( key ) {
		try {
			return JSON.parse( sessionStorage.getItem( key ) );
		} catch ( e ) {
			return null;
		}
	}

	function writeSession( key, value ) {
		try {
			if ( null === value ) {
				sessionStorage.removeItem( key );
			} else {
				sessionStorage.setItem( key, JSON.stringify( value ) );
			}
		} catch ( e ) {}
	}

	function readLocal( key ) {
		try {
			return JSON.parse( localStorage.getItem( key ) );
		} catch ( e ) {
			return null;
		}
	}

	function writeLocal( key, value ) {
		try {
			localStorage.setItem( key, JSON.stringify( value ) );
		} catch ( e ) {}
	}

	function getCookie( name ) {
		const match = document.cookie.match( new RegExp( '(?:^|;\\s*)' + name + '=([^;]*)' ) );

		return match ? match[1] : null;
	}

	function deleteCookie( name ) {
		document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
	}

	function currentUserId() {
		return parseInt( getCookie( 'swan_uid' ), 10 ) || 0;
	}

	function ownerFor( userId ) {
		return userId ? 'user:' + userId : 'guest';
	}

	function isUserList() {
		return 'guest' !== state.owner;
	}

	function removeFrom( list, id ) {
		const index = list.indexOf( id );
		if ( index !== -1 ) {
			list.splice( index, 1 );
			return true;
		}
		return false;
	}

	function dispatch( name, detail ) {
		document.dispatchEvent( new CustomEvent( name, { detail: detail } ) );
	}

	/*
	 * Public API.
	 */

	function has( id ) {
		return state.items.indexOf( parseInt( id, 10 ) ) !== -1;
	}

	function toggle( id ) {
		id = parseInt( id, 10 );
		if ( ! id ) {
			return null;
		}

		const added = ! removeFrom( state.items, id );

		if ( added ) {
			state.items.unshift( id );
			if ( addedThisPage.indexOf( id ) === -1 ) {
				addedThisPage.push( id );
			}
		}

		queueOp( added ? 'add' : 'remove', id );
		saveState();
		paint();
		scheduleSync();

		dispatch( 'swan_wishlist_changed', { productId: id, added: added } );

		return added;
	}

	/**
	 * Returns the products added on this page since the last call (for analytics-collection.js).
	 *
	 * @return {Number[]}
	 */
	function drainAdded() {
		const added   = addedThisPage;
		addedThisPage = [];
		return added;
	}

	/*
	 * Painting: sets state only, binds nothing.
	 */

	function paint( root ) {
		( root && root.querySelectorAll ? root : document ).querySelectorAll( '[data-swan-wishlist-toggle]' ).forEach( function( button ) {
			const active = has( button.getAttribute( 'data-product-id' ) );

			button.classList.toggle( 'is-in-wishlist', active );
			button.setAttribute( 'aria-pressed', active ? 'true' : 'false' );

			if ( button.hasAttribute( 'aria-label' ) ) {
				button.setAttribute( 'aria-label', active ? i18n.remove : i18n.add );
			}
		} );

		const count = state.items.length;

		document.querySelectorAll( '[data-swan-wishlist-count]' ).forEach( function( icon ) {
			if ( count ) {
				icon.setAttribute( 'data-icon-label', count );
			} else {
				icon.removeAttribute( 'data-icon-label' );
			}
			icon.classList.toggle( 'has-products', count > 0 );
		} );
	}

	function pulseHeaderIcon() {
		document.querySelectorAll( '[data-swan-wishlist-count]' ).forEach( function( icon ) {
			icon.classList.add( 'added' );
			setTimeout( function() {
				icon.classList.remove( 'added' );
			}, 500 );
		} );
	}

	/*
	 * Account sync (logged-in only).
	 */

	/**
	 * Queues an op for the server. An op cancels a pending opposite op for the same product (net: no change).
	 */
	function queueOp( type, id ) {
		if ( ! isUserList() ) {
			return;
		}

		if ( removeFrom( state.pending[ 'add' === type ? 'remove' : 'add' ], id ) ) {
			return;
		}

		if ( state.pending[ type ].indexOf( id ) === -1 ) {
			state.pending[ type ].push( id );
		}
	}

	function hasPending() {
		return state.pending.add.length > 0 || state.pending.remove.length > 0;
	}

	function scheduleSync( delay ) {
		if ( ! isUserList() || ! hasPending() ) {
			return;
		}

		clearTimeout( syncTimer );
		syncTimer = setTimeout( sync, 'number' === typeof delay ? delay : SYNC_DELAY );
	}

	function getNonce() {
		const stored = readSession( NONCE_KEY );

		return stored && stored.userId === currentUserId() ? stored.nonce : null;
	}

	/**
	 * Fetches the account list and a nonce. Resolves with the data, or null when the visitor isn't logged in.
	 *
	 * @return {Promise<Object|null>}
	 */
	function fetchState() {
		return fetch( config.ajaxUrl + '?action=swan_wishlist_state', { credentials: 'same-origin', cache: 'no-store' } )
			.then( function( response ) {
				return response.json();
			} )
			.then( function( response ) {
				const data = response && response.data ? response.data : {};

				if ( ! data.userId ) { // The cookie was stale: logged out.
					deleteCookie( 'swan_uid' );
					writeSession( NONCE_KEY, null );
					if ( isUserList() ) {
						resetTo( emptyState( 'guest' ) );
					}
					return null;
				}

				writeSession( NONCE_KEY, { userId: data.userId, nonce: data.nonce } );

				if ( isUserList() && state.owner !== ownerFor( data.userId ) ) { // Another account than the cookie said.
					resetTo( emptyState( ownerFor( data.userId ) ) );
				}

				return data;
			} );
	}

	/**
	 * Takes the server's list as the truth, then re-applies local ops that haven't reached the server yet.
	 *
	 * @param {Number[]} serverItems Server list.
	 */
	function adoptServerItems( serverItems ) {
		const items = normalizeIds( serverItems );

		state.pending.remove.forEach( function( id ) {
			removeFrom( items, id );
		} );
		state.pending.add.forEach( function( id ) {
			if ( items.indexOf( id ) === -1 ) {
				items.unshift( id );
			}
		} );

		state.items = items;
		saveState();
		paint();
		dispatch( 'swan_wishlist_updated', {} );
	}

	function resetTo( newState ) {
		state = newState;
		saveState();
		paint();
		dispatch( 'swan_wishlist_updated', {} );
	}

	function syncBody( nonce, ops ) {
		const body = new URLSearchParams();

		body.append( 'action', 'swan_wishlist_sync' );
		body.append( 'nonce', nonce );
		ops.add.forEach( function( id ) {
			body.append( 'add[]', id );
		} );
		ops.remove.forEach( function( id ) {
			body.append( 'remove[]', id );
		} );

		return body;
	}

	/**
	 * Puts ops that didn't reach the server back in front of the pending ones.
	 */
	function restoreInFlight( ops ) {
		const pending = state.pending;
		state.pending = { add: ops.add.slice(), remove: ops.remove.slice() };

		pending.add.forEach( function( id ) {
			queueOp( 'add', id );
		} );
		pending.remove.forEach( function( id ) {
			queueOp( 'remove', id );
		} );

		saveState();
	}

	/**
	 * Sends the pending ops. Ops made meanwhile queue up and go out in the next round.
	 *
	 * @param {Boolean} isRetry Whether this is the retry after an expired nonce.
	 */
	function sync( isRetry ) {
		syncTimer = null;

		if ( inFlight || ! isUserList() ) {
			return;
		}

		const ops   = state.pending;
		const owner = state.owner;
		inFlight = ops;
		state.pending = emptyPending();
		saveState();

		let succeeded = false;
		let retry     = false;

		const nonce = getNonce();

		( nonce ? Promise.resolve( nonce ) : fetchState().then( function( data ) {
			return data ? data.nonce : null;
		} ) )
			.then( function( nonce ) {
				if ( ! nonce || owner !== state.owner ) { // Logged out, or the ops belong to another account.
					return null;
				}

				return fetch( config.ajaxUrl, { method: 'POST', credentials: 'same-origin', body: syncBody( nonce, ops ) } )
					.then( function( response ) {
						if ( 403 === response.status && ! isRetry ) { // Expired nonce.
							writeSession( NONCE_KEY, null );
							retry = true;
							return null;
						}

						return response.json();
					} )
					.then( function( response ) {
						if ( response && response.success ) {
							succeeded = true;
							adoptServerItems( response.data.items );
						}
					} );
			} )
			.catch( function() {} )
			.then( function() {
				inFlight = null;

				if ( ! succeeded && owner === state.owner ) {
					restoreInFlight( ops ); // Kept in localStorage; retried now (nonce) or on the next page load.
				}

				if ( retry ) {
					sync( true );
				} else if ( succeeded ) {
					scheduleSync();
				}
			} );
	}

	/**
	 * Flushes unsent ops when the page goes away. Ops are idempotent, so a later retry is harmless.
	 */
	function flushWithBeacon() {
		if ( ! syncTimer || ! isUserList() || ! hasPending() || ! navigator.sendBeacon ) {
			return;
		}

		const nonce = getNonce();

		if ( nonce ) {
			clearTimeout( syncTimer );
			syncTimer = null;
			navigator.sendBeacon( config.ajaxUrl, syncBody( nonce, state.pending ) );
		}
	}

	/**
	 * Matches the stored list with who is logged in now (from the `swan_uid` cookie).
	 */
	function reconcileOwner() {
		const userId = currentUserId();
		const owner  = ownerFor( userId );

		if ( ! userId ) {
			if ( isUserList() ) { // Logged out: don't leave the account's list on a shared device.
				resetTo( emptyState( 'guest' ) );
			}
			maybeImportGuestList();
			return;
		}

		if ( state.owner === owner ) {
			if ( hasPending() ) {
				scheduleSync( 0 );
			} else if ( ! getNonce() ) { // Once per browser session: pick up changes made on other devices.
				fetchState().then( function( data ) {
					if ( data && ! hasPending() && ! inFlight ) {
						adoptServerItems( data.items );
					}
				} ).catch( function() {} );
			}
			return;
		}

		if ( 'guest' === state.owner ) { // Logged in: merge the guest list into the account (oldest first).
			state.pending = { add: state.items.slice().reverse(), remove: [] };
		} else { // Another account: its list isn't ours.
			state.items   = [];
			state.pending = emptyPending();
		}

		state.owner = owner;
		saveState();
		paint();

		if ( hasPending() ) {
			sync();
		} else {
			fetchState().then( function( data ) {
				if ( data ) {
					adoptServerItems( data.items );
				}
			} ).catch( function() {} );
		}
	}

	/**
	 * One-time carry-over of a guest list stored by YITH (identified by its session cookie).
	 */
	function maybeImportGuestList() {
		if ( hadStoredState ) {
			return;
		}

		const match = document.cookie.match( /(?:^|;\s*)(yith_wcwl_session_[^=]+)=([^;]*)/ );
		if ( ! match ) {
			return;
		}

		let sessionId = '';
		try {
			sessionId = JSON.parse( decodeURIComponent( match[2] ) ).session_id || '';
		} catch ( e ) {}

		saveState(); // Marks the import as done, whatever happens.

		if ( ! sessionId ) {
			deleteCookie( match[1] );
			return;
		}

		fetch( config.ajaxUrl + '?action=swan_wishlist_import_guest&session=' + encodeURIComponent( sessionId ), { credentials: 'same-origin' } )
			.then( function( response ) {
				return response.json();
			} )
			.then( function( response ) {
				const imported = normalizeIds( response && response.data ? response.data.items : [] );

				if ( imported.length && ! isUserList() ) {
					imported.reverse().forEach( function( id ) {
						if ( state.items.indexOf( id ) === -1 ) {
							state.items.unshift( id );
						}
					} );
					saveState();
					paint();
					dispatch( 'swan_wishlist_updated', {} );
				}

				deleteCookie( match[1] );
			} )
			.catch( function() {} );
	}

	/*
	 * Wishlist page.
	 */

	function initPage() {
		const page = document.querySelector( '[data-swan-wishlist-page]' );
		if ( ! page ) {
			return;
		}

		const container = page.querySelector( '[data-swan-wishlist-products]' );
		const empty     = page.querySelector( '[data-swan-wishlist-empty]' );
		const actions   = page.querySelector( '[data-swan-wishlist-actions]' );
		let rendered    = null;

		function showEmpty( isEmpty ) {
			empty.hidden   = ! isEmpty;
			actions.hidden = isEmpty;
		}

		function render() {
			const ids = state.items.slice();
			const key = ids.join( ',' );

			if ( key === rendered ) {
				return;
			}
			rendered = key;

			if ( ! ids.length ) {
				container.innerHTML = '';
				showEmpty( true );
				return;
			}

			page.classList.add( 'is-loading' );

			fetch( config.ajaxUrl + '?action=swan_wishlist_products&ids=' + ids.join( ',' ), { credentials: 'same-origin' } )
				.then( function( response ) {
					return response.json();
				} )
				.then( function( response ) {
					if ( key !== rendered ) { // A newer render started.
						return;
					}

					if ( ! response || ! response.success ) {
						rendered = null;
						return;
					}

					const html = response.data.html;

					container.innerHTML = html;

					// Products that no longer exist (deleted, unpublished) leave the list, so the count matches the page.
					const shown = normalizeIds( response.data.ids );
					const gone  = ids.filter( function( id ) {
						return shown.indexOf( id ) === -1;
					} );

					if ( gone.length ) {
						gone.forEach( function( id ) {
							removeFrom( state.items, id );
							queueOp( 'remove', id );
						} );
						rendered = state.items.join( ',' );
						saveState();
						paint();
						scheduleSync();
					}

					showEmpty( ! shown.length );

					if ( window.Flatsome && 'function' === typeof Flatsome.attach ) {
						Flatsome.attach( container );
					}
					paint( container );
				} )
				.catch( function() {
					rendered = null;
				} )
				.then( function() {
					page.classList.remove( 'is-loading' );
				} );
		}

		// Un-hearting a product here removes its tile.
		document.addEventListener( 'swan_wishlist_changed', function( e ) {
			if ( e.detail.added ) {
				rendered = null;
				render();
				return;
			}

			const button = container.querySelector( '[data-swan-wishlist-toggle][data-product-id="' + e.detail.productId + '"]' );
			const tile   = button ? button.closest( '.product-small.col, .col, .product' ) : null;

			if ( tile ) {
				tile.remove();
			}

			rendered = state.items.join( ',' );
			showEmpty( ! state.items.length );
		} );

		document.addEventListener( 'swan_wishlist_updated', render );

		initShare( page );
		render();
	}

	/**
	 * Share block (same channels and URLs as YITH's on production).
	 *
	 * The link is a saved snapshot, so it's only created on the first share action (an icon, "kopiëren", or the link
	 * field); an unchanged list reuses it without a request.
	 */
	function initShare( page ) {
		const panel = page.querySelector( '[data-swan-wishlist-share-panel]' );
		const input = page.querySelector( '[data-swan-wishlist-share-url]' );
		const copy  = page.querySelector( '[data-swan-wishlist-share-copy]' );

		if ( ! panel ) {
			return;
		}

		let pending = null;

		function listHash() {
			return state.items.slice().sort( function( a, b ) {
				return a - b;
			} ).join( ',' );
		}

		function knownUrl() {
			const cached = readLocal( SHARE_KEY );

			return cached && cached.url && cached.hash === listHash() ? cached.url : null;
		}

		function channelUrl( channel, url ) {
			const t     = encodeURIComponent( i18n.shareText || '' );
			const u     = encodeURIComponent( url );
			const image = page.querySelector( '[data-swan-wishlist-products] img' );

			switch ( channel ) {
				case 'facebook':
					return 'https://www.facebook.com/sharer.php?u=' + u + '&p[title]=' + t;
				case 'twitter':
					return 'https://twitter.com/share?url=' + u + '&text=';
				case 'pinterest':
					return 'https://pinterest.com/pin/create/button/?url=' + u + '&description=&media=' + ( image ? encodeURIComponent( image.currentSrc || image.src ) : '' );
				case 'email':
					return 'mailto:?subject=' + t + '&body=' + u + '&title=' + t;
				case 'whatsapp':
					return 'https://web.whatsapp.com/send?text=' + t + ' - ' + u;
			}

			return '#';
		}

		function fill( url ) {
			panel.querySelectorAll( '[data-swan-wishlist-share-channel]' ).forEach( function( link ) {
				link.href = url ? channelUrl( link.getAttribute( 'data-swan-wishlist-share-channel' ), url ) : '#';
			} );

			if ( input ) {
				input.value = url || '';
			}
		}

		/**
		 * @return {Promise<String>} The share URL for the current list.
		 */
		function getShareUrl() {
			const known = knownUrl();
			if ( known ) {
				return Promise.resolve( known );
			}

			if ( pending ) {
				return pending;
			}

			const ids  = state.items.slice();
			const hash = listHash();
			const body = new URLSearchParams();

			body.append( 'action', 'swan_wishlist_share' );
			ids.forEach( function( id ) {
				body.append( 'ids[]', id );
			} );

			pending = fetch( config.ajaxUrl, { method: 'POST', credentials: 'same-origin', body: body } )
				.then( function( response ) {
					return response.json();
				} )
				.then( function( response ) {
					if ( ! response || ! response.success ) {
						throw new Error( 'share failed' );
					}

					writeLocal( SHARE_KEY, { hash: hash, url: response.data.url } );
					fill( response.data.url );

					return response.data.url;
				} );

			pending.catch( function() {} ).then( function() {
				pending = null;
			} );

			return pending;
		}

		function fail() {
			alert( i18n.error );
		}

		// A link made earlier for this exact list is ready right away.
		fill( knownUrl() );
		document.addEventListener( 'swan_wishlist_changed', function() {
			fill( knownUrl() );
		} );
		document.addEventListener( 'swan_wishlist_updated', function() {
			fill( knownUrl() );
		} );

		panel.addEventListener( 'click', function( e ) {
			const link = e.target.closest( '[data-swan-wishlist-share-channel]' );
			if ( ! link || ! state.items.length ) {
				return;
			}

			const channel = link.getAttribute( 'data-swan-wishlist-share-channel' );
			const known   = knownUrl();

			if ( known ) { // The href is up to date; let the browser follow it.
				link.href = channelUrl( channel, known );
				return;
			}

			e.preventDefault();

			// Open the window within the click, so popup blockers allow it; point it at the channel once the link exists.
			const popup = '_blank' === link.target ? window.open( '', '_blank' ) : null;
			if ( popup ) {
				popup.opener = null;
			}

			getShareUrl().then( function( url ) {
				const href = channelUrl( channel, url );

				if ( popup ) {
					popup.location.href = href;
				} else {
					window.location.href = href;
				}
			} ).catch( function() {
				if ( popup ) {
					popup.close();
				}
				fail();
			} );
		} );

		function copyUrl() {
			getShareUrl().then( function( url ) {
				input.value = url;
				input.select();

				const done = function() {
					copy.textContent = i18n.copied;
				};

				if ( navigator.clipboard && navigator.clipboard.writeText ) {
					navigator.clipboard.writeText( url ).then( done ).catch( function() {
						document.execCommand( 'copy' );
						done();
					} );
				} else {
					document.execCommand( 'copy' );
					done();
				}
			} ).catch( fail );
		}

		if ( copy && input ) {
			copy.addEventListener( 'click', copyUrl );
			copy.addEventListener( 'keydown', function( e ) {
				if ( 'Enter' === e.key || ' ' === e.key ) {
					e.preventDefault();
					copyUrl();
				}
			} );
		}

		if ( input ) {
			input.addEventListener( 'focus', function() {
				if ( ! state.items.length ) {
					return;
				}

				getShareUrl().then( function( url ) {
					input.value = url;
					input.select();
				} ).catch( fail );
			} );
		}
	}

	/*
	 * Init.
	 */

	// The only click listener: delegated, so it covers tiles added at any time.
	document.addEventListener( 'click', function( e ) {
		const button = e.target.closest ? e.target.closest( '[data-swan-wishlist-toggle]' ) : null;
		if ( ! button ) {
			return;
		}

		e.preventDefault();

		if ( toggle( button.getAttribute( 'data-product-id' ) ) ) {
			pulseHeaderIcon();
		}
	} );

	// Another tab changed the list.
	window.addEventListener( 'storage', function( e ) {
		if ( STORAGE_KEY === e.key ) {
			state = loadState();
			paint();
			dispatch( 'swan_wishlist_updated', {} );
		}
	} );

	window.addEventListener( 'pagehide', flushWithBeacon );
	document.addEventListener( 'visibilitychange', function() {
		if ( 'hidden' === document.visibilityState ) {
			flushWithBeacon();
		}
	} );

	let flatsomeHooked    = false;
	let speedsearchHooked = false;

	/**
	 * Repaints after other scripts render tiles. Retried until their globals exist: this file runs before the
	 * deferred flatsome.js, and SpeedSearch may load later too. Each hook is registered once.
	 */
	function hookRenderers() {
		// Flatsome runs behaviors on content it loads (quick view, lightboxes), and SpeedSearch runs all of them
		// after every posts update; a paint-only behavior keeps hearts correct there without binding anything.
		if ( ! flatsomeHooked && window.Flatsome && 'function' === typeof Flatsome.behavior ) {
			flatsomeHooked = true;
			Flatsome.behavior( 'swan-wishlist', {
				attach: function( context ) {
					paint( context );
				},
			} );
		}

		if ( ! speedsearchHooked && window.speedsearch && 'function' === typeof speedsearch.hookAFuncAfterThePostsAddition ) {
			speedsearchHooked = true;
			speedsearch.hookAFuncAfterThePostsAddition( function() {
				paint();
			} );
		}

		paint();
	}

	function init() {
		hookRenderers();
		reconcileOwner();
		initPage();
	}

	window.swanWishlist = {
		has:        has,
		toggle:     toggle,
		items:      function() {
			return state.items.slice();
		},
		count:      function() {
			return state.items.length;
		},
		paint:      paint,
		drainAdded: drainAdded,
	};

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
		document.addEventListener( 'DOMContentLoaded', hookRenderers );
	}
	window.addEventListener( 'load', hookRenderers );
} )();
